// ─────────────────────────────────────────────────────────────────
// Glass-i — TDS / TCS Deductions register page (Stage 6P.9, Phase 5C).
//
// Server-paginated register at /erp/tds-tcs/deductions.
//
// Columns (brief §L3):
//   Deduction No · Date · Tax Type · Section · Supplier (+ GSTIN tag)
//   Source Type · Taxable Amount · Base Amount · Rate %
//   Deducted Amount · Challan Status · Certificate Status · Status
//   Voucher No · Actions (⋯)
//
// Toolbar:
//   Search (q over deduction_no / supplier_name / GSTIN /
//   source_document_no) · Supplier filter · Section · Tax Type ·
//   Date range · Status · Challan Status · Certificate Status ·
//   Clear · Refresh
//
// Per-row actions:
//   • View detail drawer (read-only summary)
//   • Cancel — only when deduction.status === 'DRAFT'
//             (RoleGate-protected on erp.tds_tcs.deductions.cancel)
//
// Backend remains the authority for ALL deduction math + lifecycle:
//   rule selection, PAN-aware rate ladder, certificate amount-used
//   bookkeeping, threshold register roll-up, voucher posting via
//   the shared voucherPosting public contract, register-only reversal
//   on Purchase Return, challan PAID lifecycle.
// This surface only reads + dispatches the cancel action.
//
// Style:
//   Mirrors PurchaseReturnsPage / SupplierPaymentRegisterPage —
//   slate-50 header strip, 22 px row density, sticky toolbar, ⋯ row-
//   actions menu. Reuses the shared GoodsReceiptsPage.css via the
//   .grn-* wrapper so no new CSS file is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Descriptions, Drawer, Dropdown,
  Input, Modal, Select, Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined, EyeOutlined,
  StopOutlined, ExclamationCircleOutlined, FileProtectOutlined
} from '@ant-design/icons';

import { tdsTcsApi } from './tdsTcsApi';
import {
  TAX_TYPES,
  DEDUCTION_STATUSES,
  CHALLAN_STATUSES,
  CERTIFICATE_STATUSES,
  PERMS,
  pickField,
  formatDate,
  formatMoney,
  formatPercent,
  taxTypeLabel,
  taxTypeColor,
  deductionStatusLabel,
  deductionStatusColor,
  challanStatusLabel,
  challanStatusColor,
  certificateStatusLabel,
  certificateStatusColor,
  sourceTypeLabel,
  canCancelDeduction
} from './tdsTcsHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const TAX_TYPE_OPTIONS = TAX_TYPES.map((v) => ({
  value: v,
  label: taxTypeLabel(v)
}));

const STATUS_OPTIONS = DEDUCTION_STATUSES.map((v) => ({
  value: v,
  label: deductionStatusLabel(v)
}));

const CHALLAN_STATUS_OPTIONS = CHALLAN_STATUSES.map((v) => ({
  value: v,
  label: challanStatusLabel(v)
}));

const CERT_STATUS_OPTIONS = CERTIFICATE_STATUSES.map((v) => ({
  value: v,
  label: certificateStatusLabel(v)
}));


// ─────────────────────────────────────────────────────────────────
// Local helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function TdsTcsDeductionsPage() {
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit perm strings drive every
  // other role. Mirrors the gating pattern used by the supplier-payment
  // + purchase-return registers.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm(PERMS.DEDUCTIONS_VIEW);
  const canCancel = hasPerm(PERMS.DEDUCTIONS_CANCEL);

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput,    setSearchInput]    = useState('');
  const [search,         setSearch]         = useState('');
  const [supplier,       setSupplier]       = useState('');
  const [section,        setSection]        = useState('');
  const [taxType,        setTaxType]        = useState(null);
  const [status,         setStatus]         = useState(null);
  const [challanStatus,  setChallanStatus]  = useState(null);
  const [certStatus,     setCertStatus]     = useState(null);
  const [dateRange,      setDateRange]      = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Drawer state — read-only detail surface. ────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow,  setDrawerRow]  = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      const supplierNeedle = (supplier || '').trim();
      if (supplierNeedle) {
        params.supplier     = supplierNeedle;
        params.supplierName = supplierNeedle;
      }
      const sectionNeedle = (section || '').trim().toUpperCase();
      if (sectionNeedle) params.sectionCode = sectionNeedle;
      if (taxType)       params.taxType = taxType;
      if (status)        params.status = status;
      if (challanStatus) params.challanStatus = challanStatus;
      if (certStatus)    params.certificateStatus = certStatus;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await tdsTcsApi.listDeductions(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load TDS / TCS deductions.'),
        key: 'tds-deductions-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    canView, page, pageSize, search, supplier, section, taxType,
    status, challanStatus, certStatus, dateRange
  ]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1. ────
  const onSupplier = (e) => { setSupplier(e.target.value); setPage(1); };
  const onSection  = (e) => { setSection(e.target.value);  setPage(1); };
  const onTaxType  = (v) => { setTaxType(v || null);       setPage(1); };
  const onStatus   = (v) => { setStatus(v || null);        setPage(1); };
  const onChallan  = (v) => { setChallanStatus(v || null); setPage(1); };
  const onCert     = (v) => { setCertStatus(v || null);    setPage(1); };
  const onDates    = (v) => { setDateRange(v || null);     setPage(1); };
  const onReset    = () => {
    setSearchInput('');
    setSearch('');
    setSupplier('');
    setSection('');
    setTaxType(null);
    setStatus(null);
    setChallanStatus(null);
    setCertStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row interactions ─────────────────────────────────────────
  const openDetail = useCallback((row) => {
    if (!row) return;
    setDrawerRow(row);
    setDrawerOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDrawerOpen(false);
    // Defer the row reset so the close animation has data to render.
    setTimeout(() => setDrawerRow(null), 200);
  }, []);

  // ── Cancel handler — confirmed via Modal with a reason prompt.
  // Backend rejects unless deduction.status === 'DRAFT'.
  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel TDS / TCS deduction?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will cancel draft deduction{' '}
            <strong>
              {pickField(row, 'deduction_no', 'deductionNo') || id}
            </strong>
            . Already-posted deductions can't be cancelled here — they
            must be reversed via the originating Purchase Return.
            Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel Deduction',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await tdsTcsApi.cancelDeduction(id, reason || undefined);
          message.success('Deduction cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel deduction.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    const rowStatus = pickField(row, 'status', 'status');

    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View detail'
    });

    if (canCancel && canCancelDeduction(rowStatus)) {
      items.push({ type: 'divider' });
      items.push({
        key:    'cancel',
        icon:   <StopOutlined />,
        label:  'Cancel',
        danger: true
      });
    }
    return items;
  }, [canCancel]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':   return openDetail(row);
      case 'cancel': return runCancel(row);
      default:       return undefined;
    }
  }, [openDetail, runCancel]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Deduction No',
      dataIndex: 'deductionNo',
      width:     200,
      render: (_v, row) => {
        const no = pickField(row, 'deduction_no', 'deductionNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openDetail(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Date',
      dataIndex: 'deductionDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {formatDate(pickField(row, 'deduction_date', 'deductionDate'))}
        </span>
      )
    },
    {
      title:     'Tax Type',
      dataIndex: 'taxType',
      width:     100,
      render: (_v, row) => {
        const v = pickField(row, 'tax_type', 'taxType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={taxTypeColor(v)} className="grn-status-tag">
            {taxTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Section',
      dataIndex: 'sectionCode',
      width:     110,
      render: (_v, row) => {
        const v = pickField(row, 'section_code', 'sectionCode');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Source Type',
      dataIndex: 'sourceType',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'source_type', 'sourceType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {sourceTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Taxable Amount',
      dataIndex: 'taxableBaseAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {formatMoney(pickField(row, 'taxable_base_amount', 'taxableBaseAmount'))}
        </span>
      )
    },
    {
      title:     'Base Amount',
      dataIndex: 'baseAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        // Some backends store the base under the same column as the
        // taxable base for TDS/TCS — fall back to taxable_base_amount
        // when an explicit base_amount column isn't present.
        const base = pickField(row, 'base_amount', 'baseAmount');
        if (base !== undefined && base !== null && base !== '') {
          return (
            <span className="grn-mono grn-line-count">
              {formatMoney(base)}
            </span>
          );
        }
        return (
          <span className="grn-mono grn-line-count">
            {formatMoney(pickField(row, 'taxable_base_amount', 'taxableBaseAmount'))}
          </span>
        );
      }
    },
    {
      title:     'Rate %',
      dataIndex: 'ruleRatePercent',
      width:     100,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {formatPercent(pickField(row, 'rule_rate_percent', 'ruleRatePercent'))}
        </span>
      )
    },
    {
      title:     'Deducted Amount',
      dataIndex: 'deductionAmount',
      width:     150,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {formatMoney(pickField(row, 'deduction_amount', 'deductionAmount'))}
        </span>
      )
    },
    {
      title:     'Challan',
      dataIndex: 'challanStatus',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'challan_status', 'challanStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={challanStatusColor(v)} className="grn-status-tag">
            {challanStatusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Certificate',
      dataIndex: 'certificateStatus',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'certificate_status', 'certificateStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={certificateStatusColor(v)} className="grn-status-tag">
            {certificateStatusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'status', 'status');
        if (!v) {
          return (
            <Tag className="grn-status-tag" color="default">Draft</Tag>
          );
        }
        return (
          <Tag color={deductionStatusColor(v)} className="grn-status-tag">
            {deductionStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Voucher No',
      dataIndex: 'accountingVoucherNo',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'accounting_voucher_no', 'accountingVoucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openDetail, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view TDS / TCS deductions."
          description={`Ask your master account to grant ${PERMS.DEDUCTIONS_VIEW}.`}
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><FileProtectOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">TDS / TCS Deductions</span>
            <span className="grn-headstrip__subtitle">
              Register of every TDS / TCS deduction posted through the
              Purchase Bill, Supplier Payment, Purchase Return and manual
              entry pipes. Backend owns deduction number allocation,
              rule resolution, PAN-aware rate ladder, certificate
              tracking, threshold register bookkeeping and voucher
              posting — this surface reads and dispatches Cancel only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search deduction no, supplier, source doc…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Supplier name / GSTIN…"
          value={supplier}
          onChange={onSupplier}
          style={{ width: 200 }}
        />
        <Input
          allowClear
          size="small"
          placeholder="Section (e.g. 194Q)"
          value={section}
          onChange={onSection}
          style={{ width: 150 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All tax types"
          value={taxType || undefined}
          onChange={onTaxType}
          options={TAX_TYPE_OPTIONS}
          style={{ width: 140 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 160 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All challan status"
          value={challanStatus || undefined}
          onChange={onChallan}
          options={CHALLAN_STATUS_OPTIONS}
          style={{ width: 170 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All certificate status"
          value={certStatus || undefined}
          onChange={onCert}
          options={CERT_STATUS_OPTIONS}
          style={{ width: 180 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'deduction_no', 'deductionNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openDetail(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No TDS / TCS deductions yet.'
            }}
          />
        </Spin>
      </Card>

      {/* Detail drawer — read-only summary. Backend exposes a richer
          detail endpoint, but the register-level read on the drawer
          is enough for the operator-facing "what does this row carry?"
          question. */}
      <Drawer
        title={
          drawerRow
            ? `Deduction ${pickField(drawerRow, 'deduction_no', 'deductionNo') || ''}`
            : 'Deduction detail'
        }
        width={640}
        open={drawerOpen}
        onClose={closeDetail}
        destroyOnClose
        extra={
          drawerRow && canCancel && canCancelDeduction(
            pickField(drawerRow, 'status', 'status')
          )
            ? (
              <Button
                danger
                size="small"
                icon={<StopOutlined />}
                onClick={() => {
                  closeDetail();
                  runCancel(drawerRow);
                }}
              >
                Cancel
              </Button>
            )
            : null
        }
      >
        {drawerRow ? (
          <Descriptions
            size="small"
            column={1}
            bordered
            labelStyle={{ width: 200, background: '#f8fafc', fontWeight: 500 }}
          >
            <Descriptions.Item label="Deduction No">
              <span className="grn-mono">
                {pickField(drawerRow, 'deduction_no', 'deductionNo') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {formatDate(pickField(drawerRow, 'deduction_date', 'deductionDate'))}
            </Descriptions.Item>
            <Descriptions.Item label="Financial Year">
              {pickField(drawerRow, 'financial_year', 'financialYear') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Tax Type">
              <Tag color={taxTypeColor(pickField(drawerRow, 'tax_type', 'taxType'))}>
                {taxTypeLabel(pickField(drawerRow, 'tax_type', 'taxType')) || '—'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Section">
              <span className="grn-mono">
                {pickField(drawerRow, 'section_code', 'sectionCode') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Source">
              {sourceTypeLabel(pickField(drawerRow, 'source_type', 'sourceType')) || '—'}
              {pickField(drawerRow, 'source_document_no', 'sourceDocumentNo')
                ? (
                  <span className="grn-mono" style={{ marginLeft: 8 }}>
                    {pickField(drawerRow, 'source_document_no', 'sourceDocumentNo')}
                  </span>
                )
                : null}
            </Descriptions.Item>
            <Descriptions.Item label="Supplier">
              {pickField(drawerRow, 'supplier_name', 'supplierName') || '—'}
              {pickField(drawerRow, 'supplier_gstin', 'supplierGstin')
                ? (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {pickField(drawerRow, 'supplier_gstin', 'supplierGstin')}
                  </Tag>
                )
                : null}
            </Descriptions.Item>
            <Descriptions.Item label="Supplier PAN">
              <span className="grn-mono">
                {pickField(drawerRow, 'supplier_pan', 'supplierPan') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Taxable Base">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'taxable_base_amount', 'taxableBaseAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Rate %">
              <span className="grn-mono">
                {formatPercent(pickField(drawerRow, 'rule_rate_percent', 'ruleRatePercent'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Deduction Amount">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'deduction_amount', 'deductionAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Net Payable">
              <span className="grn-mono">
                {formatMoney(pickField(drawerRow, 'net_payable_amount', 'netPayableAmount'))}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Payable Ledger">
              <span className="grn-mono">
                {pickField(drawerRow, 'payable_ledger_code', 'payableLedgerCode') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Voucher No">
              <span className="grn-mono">
                {pickField(drawerRow, 'accounting_voucher_no', 'accountingVoucherNo') || '—'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={deductionStatusColor(pickField(drawerRow, 'status', 'status'))}>
                {deductionStatusLabel(pickField(drawerRow, 'status', 'status')) || '—'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Challan Status">
              <Tag color={challanStatusColor(pickField(drawerRow, 'challan_status', 'challanStatus'))}>
                {challanStatusLabel(pickField(drawerRow, 'challan_status', 'challanStatus')) || '—'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Certificate Status">
              <Tag color={certificateStatusColor(pickField(drawerRow, 'certificate_status', 'certificateStatus'))}>
                {certificateStatusLabel(pickField(drawerRow, 'certificate_status', 'certificateStatus')) || '—'}
              </Tag>
            </Descriptions.Item>
            {pickField(drawerRow, 'failure_reason', 'failureReason') ? (
              <Descriptions.Item label="Failure Reason">
                {pickField(drawerRow, 'failure_reason', 'failureReason')}
              </Descriptions.Item>
            ) : null}
            {pickField(drawerRow, 'reversal_reason', 'reversalReason') ? (
              <Descriptions.Item label="Reversal Reason">
                {pickField(drawerRow, 'reversal_reason', 'reversalReason')}
              </Descriptions.Item>
            ) : null}
            {pickField(drawerRow, 'remarks', 'remarks') ? (
              <Descriptions.Item label="Remarks">
                {pickField(drawerRow, 'remarks', 'remarks')}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        ) : null}
      </Drawer>
    </div>
  );
}
